import React, { useState, useEffect } from 'react';
import { CalendarDays } from 'lucide-react';
import api from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { toast } from 'react-toastify';

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  const fetchHolidays = async () => {
    try {
      const response = await api.get('/employee/holidays');
      const allHolidays = response.data || [];
      const sortedHolidays = [...allHolidays].sort((a, b) => new Date(a.date) - new Date(b.date));
      setHolidays(sortedHolidays);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error('Failed to fetch holidays');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getDayOfWeek = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Holiday List</h1>
          <p className="text-gray-600">View company holidays across years in ascending order</p>
        </div>
        <div className="flex items-center px-4 py-2 bg-primary-50 rounded-lg">
          <CalendarDays className="h-5 w-5 text-primary-600 mr-2" />
          <span className="text-sm font-medium text-primary-700">
            {holidays.length} {holidays.length === 1 ? 'Holiday' : 'Holidays'}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <LoadingSpinner size="lg" className="mx-auto" />
        </div>
      ) : holidays.length > 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.No
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Day
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Holiday Name
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {holidays.map((holiday, index) => (
                  <tr key={holiday._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                      {formatDate(holiday.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getDayOfWeek(holiday.date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {holiday.description}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <CalendarDays className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No holidays</h3>
            <p className="mt-1 text-sm text-gray-500">
              No holidays have been scheduled yet.
            </p>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      {holidays.length > 0 && (
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg border border-primary-100 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CalendarDays className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-900">Plan Ahead</h3>
              <p className="text-sm text-gray-600 mt-1">
                Use these holidays to plan your leaves and personal commitments effectively.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Holidays;
